import React, { useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Check, ChevronDownIcon, FileText, ListChecks, Search } from 'lucide-react';

import { Sprint } from 'types/project/sprint';

interface SprintDestinationDropdownProps {
  /** Planned sprints a new item can go into; the running sprint takes no new work. */
  sprints: Sprint[];
  /** The chosen sprint; null is the Backlog. */
  value: string | null;
  onChange: (sprintUuid: string | null) => void;
  disabled?: boolean;
  className?: string;
}

/** Where a new work item lands: the Backlog or one of the planned sprints. */
const SprintDestinationDropdown: React.FC<SprintDestinationDropdownProps> = ({
  sprints,
  value,
  onChange,
  disabled = false,
  className = '',
}) => {
  const selected = sprints.find((s) => s.sprintUuid === value);
  const SelectedIcon = selected ? ListChecks : FileText;

  return (
    <Popover className="relative block w-full text-left">
      <PopoverButton
        type="button"
        disabled={disabled}
        className={`flex items-center gap-2 text-sm text-neutral hover:bg-primary-subtle transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <SelectedIcon size={16} className="text-neutral-medium shrink-0" />
        <span className="flex-1 truncate text-left">{selected?.name ?? 'Backlog'}</span>
        <ChevronDownIcon className="w-3 h-3 mr-3 shrink-0" />
      </PopoverButton>

      <PopoverPanel
        transition
        anchor="bottom start"
        className="mt-2 z-[9999] w-[var(--button-width)] rounded-lg bg-primary-variant border border-primary-subtle shadow-lg p-1 transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
      >
        {({ close }) => (
          // Mounted only while open, so the search starts empty each time.
          <DestinationOptions
            sprints={sprints}
            value={value}
            onSelect={(uuid) => {
              onChange(uuid);
              close();
            }}
          />
        )}
      </PopoverPanel>
    </Popover>
  );
};

interface DestinationOptionsProps {
  sprints: Sprint[];
  value: string | null;
  onSelect: (sprintUuid: string | null) => void;
}

const DestinationOptions: React.FC<DestinationOptionsProps> = ({ sprints, value, onSelect }) => {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const matches = term ? sprints.filter((s) => s.name.toLowerCase().includes(term)) : sprints;
  // The Backlog is always an option unless the search plainly rules it out.
  const showBacklog = !term || 'backlog'.includes(term);

  const option = (uuid: string | null, label: string, Icon: React.ElementType) => (
    <button
      key={uuid ?? 'backlog'}
      type="button"
      onClick={() => onSelect(uuid)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-neutral transition-colors hover:bg-primary-subtle ${
        uuid === value ? 'bg-primary-subtle' : ''
      }`}
    >
      <Icon size={16} className="text-neutral-medium shrink-0" />
      <span className="flex-1 truncate text-left">{label}</span>
      {uuid === value && <Check size={14} className="text-neutral-medium shrink-0" />}
    </button>
  );

  return (
    <>
      <div className="flex items-center gap-2 px-2 mb-1 h-9 rounded-md border border-primary-subtle bg-primary-light">
        <Search size={14} className="text-neutral-medium shrink-0" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sprints..."
          className="flex-1 min-w-0 bg-transparent text-sm text-neutral placeholder:text-neutral-medium outline-none"
        />
      </div>
      <div className="max-h-64 overflow-y-auto custom-scrollbar">
        {showBacklog && option(null, 'Backlog', FileText)}
        {matches.map((s) => option(s.sprintUuid, s.name, ListChecks))}
        {!showBacklog && matches.length === 0 && (
          <p className="px-3 py-2 text-sm text-neutral-medium">No sprints found</p>
        )}
      </div>
    </>
  );
};

export default SprintDestinationDropdown;
